import { Col, Row } from 'react-bootstrap';
import React, { useState } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import RecaptchaV2 from '../../../components/common/Captcha/RecaptchaV2';
import ContainerBlue from '../../../components/common/Container/ContainerBlue';
import { REACT_APP_RECAPTCHA_SITE_KEY, REACT_APP_RECAPTCHA_V2_SITE_KEY } from '../../../components/common/Routes/URLs';
import BlueCommonBtn from '../../../components/common/Buttons/BlueCommonBtn';
import GrayCommonBtn from '../../../components/common/Buttons/GrayCommonBtn';
import useRecaptchaTimer from '../../../Hooks/Recaptcha/useRecaptchaTimer';
import { useTempEdgeTranslator } from '../../../Hooks/useTranslation';
import { normalizeDate } from '../../../utils/ReGexs';
import PortalUserSummary from './PortalUserSummary';
import UserSummaryRow from './UserSummaryRow';

const GENDER_LABEL_KEYS = {
  0: 'com.tempedge.msg.label.gender.male',
  1: 'com.tempedge.msg.label.gender.female'
};

const getUserSummaryRows = (values, translate) => {
  const { account, user, edit } = values;
  const isClient = user?.role?.name?.indexOf('CLIENT') >= 0;
  const rows = [
    [
      { key: 'firstName', labelKey: 'com.tempedge.msg.label.firstname', value: account.firstName },
      { key: 'middleName', labelKey: 'com.tempedge.msg.label.middlename', value: account.middleName },
      { key: 'lastName', labelKey: 'com.tempedge.msg.label.lastname', value: account.lastName }
    ],
    [
      {
        key: 'gender',
        labelKey: 'com.tempedge.msg.label.gender',
        value: translate(GENDER_LABEL_KEYS[account.gender] ?? 'com.tempedge.msg.label.select')
      },
      { key: 'email', labelKey: 'com.tempedge.msg.label.email', value: account.email },
      { key: 'birthday', labelKey: 'com.tempedge.msg.label.birthday', value: normalizeDate(account.birthDay) }
    ]
  ];
  if (!edit) {
    rows.push([
      { key: 'role', labelKey: 'com.tempedge.msg.label.role', value: user.role?.name },
      { key: 'organization', labelKey: 'com.tempedge.msg.label.organization', value: user.orgName },
      {
        key: 'lookup',
        labelKey: isClient ? 'com.tempedge.msg.label.client' : 'com.tempedge.msg.label.office',
        value: isClient ? user.agencyclient : user.agencyoffice
      }
    ]);
  }
  return rows;
};

function WizardSecondPage({ prev, valuesFormik, handleSubmit, stepper }) {
  const [showRecaptchaV2, setShowRecaptchaV2] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [recaptchaV2Verified] = useState(false);
  const { tempedgeTranslator } = useTempEdgeTranslator();

  useRecaptchaTimer(() => {
    setShowRecaptchaV2(true);
    setIsSubmitDisabled(true); // Deshabilitar el botón de submit cuando se muestra el reCAPTCHA v2
  });

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    try {
      if (executeRecaptcha) {
        const recaptchaToken = await executeRecaptcha('wizardSecondPage');
        handleSubmit({ ...valuesFormik, token: recaptchaToken });
      } else {
        console.warn('No se pudo obtener el contexto de Google ReCaptcha.');
        handleSubmit(valuesFormik);
      }
    } catch (error) {
      console.error('Error al ejecutar reCAPTCHA:', error);
      handleSubmit(valuesFormik);
    }
  };

  const handleRecaptchaVerify = () => {
    setIsSubmitDisabled(false); // Habilitar el botón de submit cuando se complete el reCAPTCHA v2
    // Marcar el reCAPTCHA v2 como verificado
  };

  const summaryRows = getUserSummaryRows(valuesFormik, tempedgeTranslator);

  const btns = (
    <Row className="prev-next-btns-agency justify-content-center">
      <Col sm={12} md={5} lg={5}>
        <GrayCommonBtn tag="com.tempedge.msg.label.back" onClick={() => prev()} data-testid="user-wizard-second-back-button" />
      </Col>
      <Col sm={12} md={5} lg={5}>
        <BlueCommonBtn
          tag="com.tempedge.msg.label.submit"
          type="button"
          onClick={handleFormSubmit}
          disabled={isSubmitDisabled}
          data-testid="user-wizard-second-submit-button"
        />
      </Col>
    </Row>
  );

  return (
    <ContainerBlue
      title={valuesFormik?.edit ? 'com.tempedge.msg.title.updateaccount' : 'com.tempedge.msg.title.newaccount'}
      btns={btns}
      width={1280}
    >
      {stepper}
      {summaryRows.map((fields) => (
        <UserSummaryRow key={fields[0].key} fields={fields} />
      ))}
      {valuesFormik.edit && <PortalUserSummary portalUserList={valuesFormik.portalUserList} />}
      {showRecaptchaV2 && (
        <div className="form-group mt-3 d-flex justify-content-center">
          <div
            id="recaptcha-v2-container"
            style={{
              border: recaptchaV2Verified ? 'none' : '1px solid red', // Muestra borde rojo si no está verificado
              borderRadius: '4px'
            }}
          >
            <RecaptchaV2 sitekey={REACT_APP_RECAPTCHA_V2_SITE_KEY} onVerify={handleRecaptchaVerify} />
          </div>
        </div>
      )}
    </ContainerBlue>
  );
}

// Se asegura que el componente esté siempre dentro del contexto de reCAPTCHA
const WrappedWizardSecondPage = (props) => (
  <GoogleReCaptchaProvider reCaptchaKey={REACT_APP_RECAPTCHA_SITE_KEY}>
    <WizardSecondPage {...props} />
  </GoogleReCaptchaProvider>
);

export default WrappedWizardSecondPage;
